// 배차 계산기 — 공용 기본값 관리(서버 can_edit = 계산기 + 기본값 관리 권한일 때만 화면이 연다 · 막는 것은 서버).
// 세 덩어리:
//  ① 지금 계산기 화면 값을 공용 기본값으로 저장 — 사유(선택, 80자) + 두 번 눌러야 저장(첫 클릭은 4초 동안 무장만 한다).
//     첫 클릭: 오류 칸이 있거나 화면 검사(validateDefaults)에 걸리면 안내창만 · 둘 다 아니면 문구를 바꾸고 4초 타이머.
//     4초 안에 두 번째 클릭: diff·확인창 없이 PUT 1회(본문에 reason) → '저장했습니다'(바뀐 항목 0개면 '바뀐 항목이 없습니다.')
//     → 사유를 비우고 onReload({screenSaved:true}) → 이력을 다시 읽는다.
//  ② 프리셋 4종(계절 현재·동절기 · 박스 구성 현재·행거) — 화면 검사 → diff → 확인창('{label}: {before} → {after}') → PUT.
//  ③ 변경 이력 20건 — '시각 · 수정자' · 사유(없으면 '사유 없음') · 요약(파우치%·마대당·11톤 마대 수·적재율) · 바뀐 줄.
// 409(다른 사람이 먼저 바꿈)는 문구를 보인 뒤 다시 읽는다 · 422 는 프리셋 칸이면 그 칸 아래 빨간 글씨, 아니면 안내창.
// 편집 칸 이름(aria-label)은 '기본값 · ' 으로 시작한다 — 계산기 칸과 이름이 겹치지 않게.
import { useCallback, useEffect, useRef, useState } from 'react';
import { dispatchCalcAPI } from '../../api/client';
import { alertDialog, confirmDialog, toast } from '../../components/AppDialog';
import { fmtKstDateTime } from '../../utils/kstTime';
import { COLORS, btn, card, input } from '../../utils/uiStyles';
import {
  MIX_PRESET_KEYS, SEASON_KEYS, SIZES,
  fmtNum, formFromDefaults, payloadFromForm, payloadFromState, toNumber, validateDefaults,
} from './dispatchCalc';

export const HISTORY_LIMIT = 20;
export const ARM_MS = 4000;
export const REASON_MAX = 80;
const SAVE_LABEL = '현재 화면 값을 공용 기본값으로 저장';
const ARMED_LABEL = '한 번 더 누르면 모든 사용자 기본값이 바뀝니다';
const groupTitle = { margin: '12px 0 6px', fontSize: '12px', fontWeight: 600, color: COLORS.text };
const errText = { color: COLORS.dangerText, fontSize: '11px', marginTop: '3px' };
const subText = { fontSize: '12px', color: COLORS.textSub };
const SCREEN_INVALID = '화면 값을 기본값으로 저장할 수 없습니다 — ';

const detailOf = (e) => e?.response?.data?.detail;
const messageOf = (e, fallback) => {
  const d = detailOf(e);
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  if (typeof d === 'string' && d) return d;
  return fallback;
};
// 이 패널에 칸이 있는 오류(프리셋)만 칸 아래에 보인다 — 나머지는 안내창.
const isPresetField = (field) => typeof field === 'string' && field.startsWith('presets.');

/** 이력 요약 → '파우치 85% · 마대당 20개 · 11톤 마대 320개 · 적재율 80%'(요약이 없으면 '—'). */
export function summaryText(summary) {
  if (!summary || typeof summary !== 'object') return '—';
  const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? fmtNum(v) : '—');
  return `파우치 ${n(summary.pouch_pct)}% · 마대당 ${n(summary.per_sack)}개 · 11톤 마대 ${n(summary.sacks_per_truck)}개 · 적재율 ${n(summary.load_pct)}%`;
}

export default function DispatchDefaultsPanel({ server, calcState, onClose, onReload }) {
  const [form, setForm] = useState(() => formFromDefaults(server.defaults));
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [armed, setArmed] = useState(false);
  const [history, setHistory] = useState(null);   // null = 불러오는 중
  const [historyError, setHistoryError] = useState('');
  const armTimer = useRef(null);

  // 다시 읽어 revision 이 바뀌면(저장·409) 프리셋 칸을 새 공용 기본값으로 맞춘다.
  useEffect(() => {
    setForm(formFromDefaults(server.defaults));
    setFieldErrors({});
  }, [server.revision]);

  // 닫거나 화면을 떠나면 무장 타이머를 푼다.
  useEffect(() => () => {
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = null;
  }, []);

  const loadHistory = useCallback(() => {
    dispatchCalcAPI.history(HISTORY_LIMIT)
      .then((r) => { setHistory(Array.isArray(r.data?.items) ? r.data.items : []); setHistoryError(''); })
      .catch(() => { setHistory([]); setHistoryError('변경 이력을 불러오지 못했습니다.'); });
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const disarm = () => {
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = null;
    setArmed(false);
  };

  const setValue = (path, raw) => {
    setForm((f) => ({ ...f, values: { ...f.values, [path]: raw } }));
    // 검사는 첫 오류 하나만 보인다 — 칸을 고치면 지운다(다음 저장에서 다시 검사한다).
    setFieldErrors((errs) => (Object.keys(errs).length ? {} : errs));
  };

  const reload = async (opts) => {
    await onReload(opts);
    loadHistory();
  };

  const failed = async (e, source) => {
    const status = e?.response?.status;
    const d = detailOf(e);
    if (status === 409) {
      await alertDialog(messageOf(e, '다른 사람이 먼저 기본값을 바꿨습니다. 다시 불러온 뒤 저장하세요.'));
      await reload();
      return;
    }
    if (status === 422 && d && typeof d === 'object' && d.field) {
      if (source === 'form' && isPresetField(d.field)) setFieldErrors({ [d.field]: d.message });
      else await alertDialog((source === 'screen' ? SCREEN_INVALID : '') + d.message);
      return;
    }
    await alertDialog(`저장하지 못했습니다: ${messageOf(e, e?.message || '알 수 없는 오류')}`);
  };

  // ① 지금 화면 값 — 두 번 눌러야 저장한다.
  const screenPayload = () => payloadFromState(calcState, server.defaults.presets);
  const saveScreen = async () => {
    if (busy) return;
    if (Object.keys(calcState?.errors || {}).length) {
      disarm();
      await alertDialog('오류 표시된 칸을 먼저 고치세요.');
      return;
    }
    const invalid = validateDefaults(screenPayload());
    if (invalid) {
      disarm();
      await alertDialog(SCREEN_INVALID + invalid.message);
      return;
    }
    if (!armed) {
      setArmed(true);
      armTimer.current = setTimeout(() => {
        armTimer.current = null;
        setArmed(false);
      }, ARM_MS);
      return;
    }
    disarm();
    const body = { ...screenPayload(), base_revision: server.revision, reason: reason.trim() };
    setBusy(true);
    try {
      const r = await dispatchCalcAPI.save(body);
      const changes = Array.isArray(r?.data?.changes) ? r.data.changes : [];
      if (changes.length) toast('저장했습니다');
      else toast('바뀐 항목이 없습니다.', 'info');
      setReason('');
      await reload({ screenSaved: true });
    } catch (e) {
      await failed(e, 'screen');
    } finally {
      setBusy(false);
    }
  };

  // ② 프리셋 — 화면 검사 → diff → 확인창 → PUT.
  const savePresets = async () => {
    const payload = payloadFromForm(form);
    const invalid = validateDefaults(payload);
    if (invalid) {
      if (isPresetField(invalid.field)) setFieldErrors({ [invalid.field]: invalid.message });
      else await alertDialog(invalid.message);
      return;
    }
    setFieldErrors({});
    const body = { ...payload, base_revision: server.revision, reason: reason.trim() };
    setBusy(true);
    try {
      const preview = await dispatchCalcAPI.diff(body);
      const changes = Array.isArray(preview.data?.changes) ? preview.data.changes : [];
      if (!changes.length) {
        toast('바뀐 항목이 없습니다.', 'info');
        return;
      }
      const lines = changes.map((c) => `${c.label}: ${c.before} → ${c.after}`);
      const ok = await confirmDialog(
        `공용 기본값 ${changes.length}개 항목을 바꿉니다. 모든 사용자의 계산기에 적용됩니다.\n\n${lines.join('\n')}`,
        { okText: '저장' },
      );
      if (!ok) return;
      await dispatchCalcAPI.save(body);
      toast('저장했습니다');
      setReason('');
      await reload();
    } catch (e) {
      await failed(e, 'form');
    } finally {
      setBusy(false);
    }
  };

  // 편집 칸 하나(함수로 그린다 — 렌더 안에서 컴포넌트를 만들면 입력 중 칸이 새로 그려져 초점이 빠진다).
  const field = (path, label, width = '110px') => {
    const err = fieldErrors[path];
    const id = `dd-${path.replace(/\./g, '-')}`;
    return (
      <div key={path} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <span style={{ fontSize: '11px', color: COLORS.textSub }}>{label}</span>
        <input id={id} type="text" inputMode="decimal" aria-label={`기본값 · ${label}`} aria-invalid={err ? 'true' : 'false'}
          aria-describedby={err ? `${id}-err` : undefined} value={form.values[path]}
          onChange={(e) => setValue(path, e.target.value)}
          style={{ ...input, width, padding: '5px 7px', fontSize: '13px', borderColor: err ? COLORS.danger : COLORS.inputBorder }} />
        {err && <span id={`${id}-err`} style={errText}>{err}</span>}
      </div>
    );
  };
  // 묶음 칸(구성비 합계·다섯 칸 등)의 오류 — 서버·화면 검사의 field 가 칸이 아니라 묶음 이름일 때.
  const groupError = (path) => (fieldErrors[path] ? <div style={errText}>{fieldErrors[path]}</div> : null);
  const sumOf = (prefix) => {
    const values = SIZES.map((i) => toNumber(form.values[`${prefix}.${i}`]));
    return values.every((v) => typeof v === 'number' && Number.isFinite(v)) ? fmtNum(values.reduce((a, b) => a + b, 0)) : '—';
  };
  const row = (children) => <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>{children}</div>;

  return (
    <section aria-label="기본값 관리" style={{ ...card, marginBottom: '12px', borderColor: COLORS.primaryBorder }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '14px' }}>기본값 관리</h3>
        <span style={{ fontSize: '11px', color: COLORS.textSub }}>모든 사용자의 계산기 시작값입니다. 지금 계산기 화면 값을 그대로 공용 기본값으로 저장합니다.</span>
      </div>

      <div style={groupTitle}>현재 화면 값 저장</div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" aria-label="변경 사유(선택, 80자)" placeholder="변경 사유(선택, 80자)" maxLength={REASON_MAX}
          value={reason} onChange={(e) => setReason(e.target.value)}
          style={{ ...input, width: '320px', padding: '5px 7px', fontSize: '13px' }} />
        <button type="button" onClick={saveScreen} disabled={busy}
          style={btn(armed ? 'danger' : 'accent', 'sm')}>{armed ? ARMED_LABEL : SAVE_LABEL}</button>
      </div>
      <div style={{ ...subText, fontSize: '11px', marginTop: '4px' }}>
        실수로 바뀌지 않게 두 번 눌러야 저장됩니다(첫 클릭 뒤 4초 안에 한 번 더).
      </div>

      <div style={groupTitle}>계절 프리셋</div>
      {SEASON_KEYS.map(([key, name]) => (
        <div key={key} style={{ marginBottom: '6px' }}>
          {row([
            field(`presets.season.${key}.pouch_pct`, `계절 ${name} · 파우치 비율(%)`),
            field(`presets.season.${key}.per_sack`, `계절 ${name} · 마대당 파우치(개)`),
          ])}
        </div>
      ))}

      <div style={groupTitle}>박스 구성 프리셋</div>
      {MIX_PRESET_KEYS.map(([key, name]) => (
        <div key={key} style={{ marginBottom: '6px' }}>
          {row(<>
            {SIZES.map((i) => field(`presets.mix.${key}.${i}`, `${name.replace('(%)', '')} ${i + 1}호(%)`, '80px'))}
            <span data-testid={`dd-mix-sum-${key}`} style={{ fontSize: '12px', color: COLORS.textSub, paddingBottom: '7px' }}>합계 {sumOf(`presets.mix.${key}`)}</span>
          </>)}
          {groupError(`presets.mix.${key}`)}
        </div>
      ))}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
        <button type="button" onClick={savePresets} disabled={busy} style={btn('primary', 'sm')}>프리셋 저장</button>
        <button type="button" onClick={onClose} style={btn('secondary', 'sm')}>닫기</button>
      </div>

      <section aria-label="변경 이력" style={{ marginTop: '16px', borderTop: `1px solid ${COLORS.borderSoft}`, paddingTop: '10px' }}>
        <h4 style={{ margin: '0 0 6px', fontSize: '13px' }}>변경 이력(최근 {HISTORY_LIMIT}건)</h4>
        {history === null && <div style={{ fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</div>}
        {historyError && <div style={errText}>{historyError}</div>}
        {history !== null && !historyError && history.length === 0 && (
          <div style={{ fontSize: '12px', color: COLORS.textMuted }}>변경 이력이 없습니다.</div>
        )}
        {history !== null && history.length > 0 && (
          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {history.map((item) => (
              <li key={item.id} data-testid="dd-history-item" style={{ padding: '6px 0', borderBottom: `1px solid ${COLORS.borderSoft}` }}>
                <div style={{ fontSize: '12px', fontWeight: 600 }}>
                  {`${fmtKstDateTime(item.changed_at) || '—'} · ${item.changed_by_name || '—'}`}
                </div>
                <div data-testid="dd-history-reason" style={{ fontSize: '12px', color: item.reason ? COLORS.text : COLORS.textMuted }}>
                  {item.reason || '사유 없음'}
                </div>
                <div data-testid="dd-history-summary" style={subText}>{summaryText(item.summary)}</div>
                <ul style={{ margin: '2px 0 0', paddingLeft: '16px', fontSize: '12px', color: COLORS.textSub }}>
                  {(item.changes || []).map((c) => (
                    <li key={c.key}>{`${c.label}: ${c.before} → ${c.after}`}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </section>
    </section>
  );
}
